import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { toast } from '@/hooks/use-toast'
import { useState } from 'react'
import AuthLayout from '@/views/auth-layout'
import { Card } from '@/components/ui/card'
import { forgotPassword } from '@/api/auth'
import { handleServerError } from '@/utils/handle-server-error'

export default function ForgotPasswordPage() {
  const { t } = useTranslation()
  const nav = useNavigate()
  const [isLoading, setIsLoading] = useState(false)

  const formSchema = z.object({
    username: z.string().min(1, { message: t('enter_user') }),
    email: z.string().email({ message: t('invalid_email') }),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: '',
      email: '',
    },
  })

  async function onSubmit(data: z.infer<typeof formSchema>) {
    setIsLoading(true)
    try {
      await forgotPassword(data.username, data.email)
      toast({
        title: t('reset_link_sent_title'),
        description: t('reset_link_sent_desc'),
      })
      nav({ to: '/auth/reset-link-sent' })
    } catch (e) {
      handleServerError(e)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthLayout>
      <Card className="p-6">
        <div className="flex flex-col space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('forgot_password_title')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('forgot_password_desc')}
          </p>
        </div>
        <div className="grid gap-6 pt-4">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="grid gap-2">
                <FormField
                  control={form.control}
                  name="username"
                  render={({ field }) => (
                    <FormItem className="space-y-1 text-start">
                      <FormLabel>{t('user')}</FormLabel>
                      <FormControl>
                        <Input placeholder={t('user_placeholder')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="space-y-1 text-start">
                      <FormLabel>{t('email')}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t('email_placeholder')}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button className="mt-2" disabled={isLoading}>
                  {isLoading ? t('sending') : t('send_reset_link')}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </Card>
    </AuthLayout>
  )
}
